import React from 'react';
import { Link } from 'react-router-dom';
import { studentGoals, currentUser } from '../utils/mockData';
import { TargetIcon, ClockIcon, CheckCircleIcon, XCircleIcon, AlertCircleIcon } from 'lucide-react';
const StudentDashboard: React.FC = () => {
  // Filter goals by their status
  const pendingGoals = studentGoals.filter(goal => goal.studentId === currentUser.id && goal.status === 'pending');
  const submittedGoals = studentGoals.filter(goal => goal.studentId === currentUser.id && goal.status === 'submitted');
  const completedGoals = studentGoals.filter(goal => goal.studentId === currentUser.id && goal.status === 'approved');
  // Status badge component
  const StatusBadge = ({
    status
  }: {
    status: string;
  }) => {
    switch (status) {
      case 'pending':
        return <span className="flex items-center text-yellow-700 bg-yellow-100 px-2 py-1 rounded-full text-xs" data-id="element-940">
            <ClockIcon size={14} className="mr-1" data-id="element-941" />
            未提交
          </span>;
      case 'submitted':
        return <span className="flex items-center text-blue-700 bg-blue-100 px-2 py-1 rounded-full text-xs" data-id="element-942">
            <AlertCircleIcon size={14} className="mr-1" data-id="element-943" />
            待审核
          </span>;
      case 'approved':
        return <span className="flex items-center text-green-700 bg-green-100 px-2 py-1 rounded-full text-xs" data-id="element-944">
            <CheckCircleIcon size={14} className="mr-1" data-id="element-945" />
            已通过
          </span>;
      case 'rejected':
        return <span className="flex items-center text-red-700 bg-red-100 px-2 py-1 rounded-full text-xs" data-id="element-946">
            <XCircleIcon size={14} className="mr-1" data-id="element-947" />
            未通过
          </span>;
      default:
        return null;
    }
  };
  return <div className="max-w-5xl mx-auto" data-id="element-948">
      <div className="flex justify-between items-center mb-8" data-id="element-949">
        <h1 className="text-2xl font-bold text-gray-800" data-id="element-950">我的学习目标</h1>
        <Link to="/submit-goal" className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors" data-id="element-951">
          提交新目标
        </Link>
      </div>
      <div className="bg-white rounded-lg shadow p-6 mb-8" data-id="element-952">
        <div className="flex items-center mb-4" data-id="element-953">
          <img src={currentUser.avatar} alt={currentUser.name} className="w-12 h-12 rounded-full mr-4" data-id="element-954" />
          <div data-id="element-955">
            <h2 className="text-lg font-medium" data-id="element-956">{currentUser.name}</h2>
            <p className="text-gray-500" data-id="element-957">{currentUser.email}</p>
          </div>
          <div className="ml-auto flex items-center" data-id="element-958">
            <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium mr-2" data-id="element-959">
              {currentUser.points} 积分
            </span>
            <span className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-sm font-medium" data-id="element-960">
              {currentUser.badges.length} 枚勋章
            </span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8" data-id="element-961">
        <div className="bg-white rounded-lg shadow p-6" data-id="element-962">
          <h2 className="text-lg font-medium mb-4 flex items-center" data-id="element-963">
            <TargetIcon className="mr-2 text-blue-500" size={20} data-id="element-964" />
            进行中的目标 ({pendingGoals.length})
          </h2>
          {pendingGoals.length > 0 ? <div className="space-y-4" data-id="element-965">
              {pendingGoals.map(goal => <div key={goal.id} className="border border-gray-200 rounded-lg p-4" data-id="element-966">
                  <div className="flex justify-between" data-id="element-967">
                    <h3 className="font-medium text-gray-800" data-id="element-968">{goal.title}</h3>
                    <StatusBadge status={goal.status} data-id="element-969" />
                  </div>
                  <p className="text-gray-600 text-sm mt-2" data-id="element-970">
                    {goal.description}
                  </p>
                  <div className="flex justify-between items-center mt-4" data-id="element-971">
                    <span className="text-xs text-gray-500" data-id="element-972">
                      截止日期: {goal.deadline}
                    </span>
                    <Link to={`/submit-completion/${goal.id}`} className="bg-green-600 text-white text-sm px-3 py-1 rounded hover:bg-green-700 transition-colors" data-id="element-973">
                      提交完成
                    </Link>
                  </div>
                </div>)}
            </div> : <p className="text-gray-500 text-center py-4" data-id="element-974">暂无进行中的目标</p>}
        </div>
        <div className="bg-white rounded-lg shadow p-6" data-id="element-975">
          <h2 className="text-lg font-medium mb-4 flex items-center" data-id="element-976">
            <AlertCircleIcon className="mr-2 text-yellow-500" size={20} data-id="element-977" />
            待审核的目标 ({submittedGoals.length})
          </h2>
          {submittedGoals.length > 0 ? <div className="space-y-4" data-id="element-978">
              {submittedGoals.map(goal => <div key={goal.id} className="border border-gray-200 rounded-lg p-4" data-id="element-979">
                  <div className="flex justify-between" data-id="element-980">
                    <h3 className="font-medium text-gray-800" data-id="element-981">{goal.title}</h3>
                    <StatusBadge status={goal.status} data-id="element-982" />
                  </div>
                  <p className="text-gray-600 text-sm mt-2" data-id="element-983">
                    {goal.description}
                  </p>
                  <div className="mt-4" data-id="element-984">
                    <span className="text-xs text-gray-500" data-id="element-985">
                      截止日期: {goal.deadline}
                    </span>
                  </div>
                </div>)}
            </div> : <p className="text-gray-500 text-center py-4" data-id="element-986">暂无待审核的目标</p>}
        </div>
      </div>
      <div className="bg-white rounded-lg shadow p-6" data-id="element-987">
        <h2 className="text-lg font-medium mb-4 flex items-center" data-id="element-988">
          <CheckCircleIcon className="mr-2 text-green-500" size={20} data-id="element-989" />
          已完成的目标 ({completedGoals.length})
        </h2>
        {completedGoals.length > 0 ? <div className="space-y-4" data-id="element-990">
            {completedGoals.map(goal => <div key={goal.id} className="border border-gray-200 rounded-lg p-4" data-id="element-991">
                <div className="flex justify-between" data-id="element-992">
                  <h3 className="font-medium text-gray-800" data-id="element-993">{goal.title}</h3>
                  <StatusBadge status={goal.status} data-id="element-994" />
                </div>
                <p className="text-gray-600 text-sm mt-2" data-id="element-995">{goal.description}</p>
                <div className="mt-4" data-id="element-996">
                  <span className="text-xs text-gray-500" data-id="element-997">
                    截止日期: {goal.deadline}
                  </span>
                </div>
              </div>)}
          </div> : <p className="text-gray-500 text-center py-4" data-id="element-998">暂无已完成的目标</p>}
      </div>
    </div>;
};
export default StudentDashboard;